'use client';

import * as React from 'react';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { Select as SelectPrimitive } from 'radix-ui';
import { cn } from '@/shared/lib/utils';

function Select(props: React.ComponentProps<typeof SelectPrimitive.Root>) {
    return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup(props: React.ComponentProps<typeof SelectPrimitive.Group>) {
    return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue(props: React.ComponentProps<typeof SelectPrimitive.Value>) {
    return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

const SelectTrigger = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
        size?: 'sm' | 'default';
    }
>(function SelectTrigger({ className, children, size = 'default', ...props }, ref) {
    return (
        <SelectPrimitive.Trigger
            ref={ref}
            data-slot="select-trigger"
            data-size={size}
            className={cn(
                'flex w-fit items-center justify-between gap-2 whitespace-nowrap rounded-md border border-border bg-transparent px-3 text-sm text-foreground shadow-sm transition-[color,box-shadow] outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-muted [&>span]:line-clamp-1 [&>span]:flex [&>span]:items-center [&>span]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:text-muted',
                size === 'sm' ? 'h-8' : 'h-9',
                className,
            )}
            {...props}
        >
            {children}
            <SelectPrimitive.Icon asChild>
                <ChevronDownIcon className="h-4 w-4" />
            </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
    );
});

const SelectContent = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(function SelectContent({ className, children, position = 'popper', ...props }, ref) {
    return (
        <SelectPrimitive.Portal>
            <SelectPrimitive.Content
                ref={ref}
                data-slot="select-content"
                position={position}
                className={cn(
                    'relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border border-border bg-panel text-foreground shadow-panel data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                    position === 'popper'
                    && 'origin-[var(--radix-select-content-transform-origin)] data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
                    className,
                )}
                {...props}
            >
                <SelectScrollUpButton />
                <SelectPrimitive.Viewport
                    className={cn(
                        'p-1',
                        position === 'popper' && 'min-w-[var(--radix-select-trigger-width)] w-full',
                    )}
                >
                    {children}
                </SelectPrimitive.Viewport>
                <SelectScrollDownButton />
            </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
    );
});

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
    return <SelectPrimitive.Label data-slot="select-label" className={cn('px-2 py-1.5 text-xs font-medium text-muted', className)} {...props} />;
}

const SelectItem = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
        showIndicator?: boolean;
        useTextWrapper?: boolean;
    }
>(function SelectItem({ className, children, showIndicator = true, useTextWrapper = true, ...props }, ref) {
    return (
        <SelectPrimitive.Item
            ref={ref}
            data-slot="select-item"
            className={cn(
                'relative flex w-full cursor-default select-none items-center gap-2 rounded-sm py-1.5 pr-2 text-sm outline-none focus:bg-accent/15 focus:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                showIndicator ? 'pl-8' : 'pl-3',
                className,
            )}
            {...props}
        >
            {showIndicator ? (
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    <SelectPrimitive.ItemIndicator>
                        <CheckIcon className="h-4 w-4" />
                    </SelectPrimitive.ItemIndicator>
                </span>
            ) : null}
            {useTextWrapper ? <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText> : children}
        </SelectPrimitive.Item>
    );
});

function SelectSeparator({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Separator>) {
    return <SelectPrimitive.Separator data-slot="select-separator" className={cn('-mx-1 my-1 h-px bg-white/10', className)} {...props} />;
}

function SelectScrollUpButton(props: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
    return (
        <SelectPrimitive.ScrollUpButton
            data-slot="select-scroll-up-button"
            className="flex cursor-default items-center justify-center py-1"
            {...props}
        >
            <ChevronUpIcon className="h-4 w-4" />
        </SelectPrimitive.ScrollUpButton>
    );
}

function SelectScrollDownButton(props: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
    return (
        <SelectPrimitive.ScrollDownButton
            data-slot="select-scroll-down-button"
            className="flex cursor-default items-center justify-center py-1"
            {...props}
        >
            <ChevronDownIcon className="h-4 w-4" />
        </SelectPrimitive.ScrollDownButton>
    );
}

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
SelectContent.displayName = SelectPrimitive.Content.displayName;
SelectItem.displayName = SelectPrimitive.Item.displayName;

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
};